import { API_ENDPOINTS } from '../config/api'

const SS_KEY = 'adel_site_chat_v1'
const QUIZ_KEY = 'adel_quiz_chat_v1'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

export const SITE_CHAT_GREETING =
  "Hi — I'm Sarah from ADEL. Tell me about the business and where new inquiries come in. If it looks like a fit, I'll take a number so we can continue by text."

function chatUrl(suffix, extraQuery) {
  const base = API_ENDPOINTS.SITE_CHAT
  const u = new URL(base, typeof window !== 'undefined' ? window.location.origin : 'https://adel.company')
  u.pathname = `${u.pathname.replace(/\/$/, '')}${suffix}`
  if (extraQuery) {
    Object.entries(extraQuery).forEach(([k, v]) => {
      if (v != null && v !== '') u.searchParams.set(k, String(v))
    })
  }
  return u.toString()
}

async function fetchJson(url, init = {}, ms = 20000) {
  const ctrl = new AbortController()
  const t = setTimeout(() => ctrl.abort(), ms)
  try {
    const res = await fetch(url, {
      ...init,
      signal: ctrl.signal,
      headers: { Accept: 'application/json', ...(init.headers || {}) },
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err = new Error(data.error || `http_${res.status}`)
      err.status = res.status
      err.data = data
      throw err
    }
    return data
  } finally {
    clearTimeout(t)
  }
}

function readStored(key) {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.clientId || !parsed?.sessionToken) return null
    if (parsed.expiresAt && Date.parse(parsed.expiresAt) < Date.now()) {
      localStorage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeStored(key, session) {
  if (typeof window === 'undefined' || !session) return
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        clientId: session.clientId,
        sessionToken: session.sessionToken,
        region: session.region || 'EN',
        expiresAt: session.expiresAt || new Date(Date.now() + TTL_MS).toISOString(),
      }),
    )
  } catch {
    /* ignore quota */
  }
}

export function loadSiteChatSession() {
  return readStored(SS_KEY)
}

export function saveSiteChatSession(session) {
  writeStored(SS_KEY, session)
}

export function clearSiteChatSession() {
  if (typeof window === 'undefined') return
  localStorage.removeItem(SS_KEY)
}

export function loadQuizChatSession() {
  return readStored(QUIZ_KEY)
}

export function saveQuizChatSession(session) {
  writeStored(QUIZ_KEY, session)
}

export async function siteChatCreateSession(region = 'EN') {
  const data = await fetchJson(
    chatUrl('/session'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region }),
    },
    15000,
  )
  if (!data.clientId || !data.sessionToken) throw new Error('bad_session')
  return {
    clientId: data.clientId,
    sessionToken: data.sessionToken,
    region: data.region || region,
    expiresAt: data.expiresAt,
  }
}

export async function siteChatSend(text, session, ms = 22000) {
  if (!session?.clientId || !session?.sessionToken) throw new Error('missing_session')
  return fetchJson(
    chatUrl('/message'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: session.clientId,
        sessionToken: session.sessionToken,
        text,
      }),
    },
    ms,
  )
}

export async function siteChatHistory(session) {
  if (!session?.clientId || !session?.sessionToken) throw new Error('missing_session')
  return fetchJson(
    chatUrl('/history', {
      clientId: session.clientId,
      sessionToken: session.sessionToken,
    }),
    { method: 'GET' },
    12000,
  )
}

export async function requestQuizRecommendation(quiz) {
  const existing = loadQuizChatSession()
  const session = existing || (await siteChatCreateSession())
  if (!existing) saveQuizChatSession(session)
  const text = [
    'I completed the implementation quiz on adel.company.',
    `Business type: ${quiz.industry || 'not specified'}`,
    `What I want to automate: ${quiz.goalLabel || quiz.goal || 'not specified'}`,
    `Channels: ${quiz.channelsLabel || 'not specified'}`,
    `Monthly inquiries: ${quiz.volumeLabel || 'not specified'}`,
    `Internal pick: ${quiz.product || 'ADEL AI OS'} (${quiz.price || 'from $197/mo'}).`,
    'Write one short recommendation: what we should implement for this project, in what order. Then say implementation starts from $500, I should leave contacts, and a specialist will reach out to book a meeting. Do not ask extra questions in this reply.',
  ].join('\n')
  const r = await siteChatSend(text, session, 18000)
  return { session, reply: (r.reply || '').trim() }
}

export async function sendQuizContact(session, { name, phone, email }) {
  if (!session?.clientId) throw new Error('missing_session')
  const lines = [
    name ? `My name is ${name}.` : null,
    `My phone number is ${phone}.`,
    email ? `Email: ${email}.` : null,
    'Please have a specialist contact me and book a meeting.',
  ].filter(Boolean)
  return siteChatSend(lines.join(' '), session, 18000)
}
